import { isValidElement, type ReactNode } from 'react';
import { Focusable, Tooltip as AriaTooltip, TooltipTrigger } from 'react-aria-components';
import styles from './Tooltip.module.css';

export interface TooltipWrapProps {
  /** Tooltip content; kept short (one or two sentences). */
  content: ReactNode;
  /** A focusable react-aria trigger (e.g. our Button). */
  children: ReactNode;
  /** Delay before opening, in ms. */
  delay?: number;
}

export interface DisabledTipProps extends TooltipWrapProps {
  /** Accessible name for the focusable unavailable-control wrapper. */
  label?: string;
}

/**
 * Tooltip: opens on keyboard focus and hover, closes with Escape, and wires
 * aria-describedby on the trigger (react-aria-components).
 */
export function TooltipWrap({ content, children, delay = 400 }: TooltipWrapProps) {
  return (
    <TooltipTrigger delay={delay} closeDelay={100}>
      {children}
      <AriaTooltip offset={8} className={styles.tooltip}>
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}

/**
 * Disabled React Aria controls remain tabbable with aria-disabled, so the
 * tooltip can describe the unavailable action without an extra focus stop.
 */
export function DisabledTip({ content, children, label, delay = 400 }: DisabledTipProps) {
  const accessibleLabel = label ?? readChildLabel(children) ?? 'Unavailable action';
  return (
    <TooltipTrigger delay={delay} closeDelay={100}>
      <Focusable>
        <span
          className={styles.trigger}
          role="button"
          aria-label={accessibleLabel}
          aria-disabled="true"
          tabIndex={0}
        >
          <span aria-hidden="true">{children}</span>
        </span>
      </Focusable>
      <AriaTooltip offset={8} className={styles.tooltip}>
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}

function readChildLabel(child: ReactNode): string | undefined {
  if (typeof child === 'string' || typeof child === 'number') return String(child).trim();
  if (Array.isArray(child)) {
    const label = child.map(readChildLabel).filter(Boolean).join(' ').trim();
    return label || undefined;
  }
  if (!isValidElement<{ children?: ReactNode; 'aria-label'?: string }>(child)) return undefined;
  const props = child.props;
  return props['aria-label'] ?? readChildLabel(props.children);
}
